import React, { useState, useCallback } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useProcessing } from '../../context/ProcessingContext';
import DataTabs from './DataTabs';



const COLOR_SCHEMES = [
  { value: 'terrain', label: 'Terrain' },
  { value: 'viridis', label: 'Viridis' },
  { value: 'plasma', label: 'Plasma' },
  { value: 'magma', label: 'Magma' }
];

const POINT_STYLES = [
  { value: 'o', label: '●' },
  { value: 's', label: '■' },
  { value: '^', label: '▲' },
  { value: 'D', label: '◆' }
];

export const Gridding: React.FC = () => {

  const { state, dispatch } = useProcessing();
  const [selectedSize, setSelectedSize] = useState<64 | 128>(128);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [plotConfig, setPlotConfig] = useState({
    plotTitle: '',
    legendTitle: 'Z Value',
    colorScheme: 'terrain',
    pointSize: 50,
    opacity: 0.7,
    pointStyle: 'o',
    backgroundColor: '#ffffff',
    invertColorScheme: false
  });

  // Local state for input fields
  const [plotTitle, setPlotTitle] = useState(plotConfig.plotTitle);
  const [legendTitle, setLegendTitle] = useState(plotConfig.legendTitle);

  const handleTitleUpdate = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      updatePlot({ 
        plotTitle: plotTitle,
        legendTitle: legendTitle 
      });
    }
  };

  const updatePlot = async (newConfig: Partial<typeof plotConfig>) => {
    if (!state.rawData?.data) return;
    
    const updatedConfig = { ...plotConfig, ...newConfig };
    setPlotConfig(updatedConfig);

    try {
      const response = await fetch('/api/update-plot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          points: state.rawData.data,
          config: updatedConfig,
          geographic_metadata: state.rawData.geographic_metadata
        })
      });

      if (!response.ok) throw new Error(await response.text());

      const result = await response.json();
      
      dispatch({
        type: 'SET_RAW_DATA',
        payload: {
          ...state.rawData,
          plot: result.plot
        }
      });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to update plot'
      });
    }
  };

  const processGrid = useCallback(async () => {
    if (!state.rawData?.data || isProcessing) return;

    setIsProcessing(true);
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await fetch('/api/grid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          points: state.rawData.data,
          gridSize: selectedSize,
          geographic_metadata: state.rawData.geographic_metadata,
          visualization: plotConfig
        })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const result: GridResponse = await response.json();

      dispatch({
        type: 'SET_GRIDDED_DATA',
        payload: {
          grid: result.grid,
          plot: result.plot,
          histogram: result.histogram,
          stats: result.stats,
          dimensions: result.dimensions,
          metadata: result.metadata
        }
      });

      dispatch({ type: 'SET_VIEW_STEP', payload: 'interpolate' });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Grid processing failed'
      });
    } finally {
      setIsProcessing(false);
    }
  }, [state.rawData, selectedSize, dispatch, plotConfig]);

  if (!state.rawData?.data) {
    return <div className="empty-state">No data available. Please upload data first.</div>;
  }

  return (
    <div className="panel-section">
      <PanelGroup direction="horizontal">
        <Panel defaultSize={65} minSize={30}>
          <div className="grid-main-content">
            <div className="data-visualization">
              <img 
                src={`data:image/png;base64,${state.rawData.plot}`}
                alt="Data visualization"
                className="preview-image"
              />
            </div>

            <div className="grid-controls-container">
              <div className="resolution-options">
                <div className="resolution-buttons">
                  <button
                    className={`resolution-button ${selectedSize === 64 ? 'active' : ''}`}
                    onClick={() => setSelectedSize(64)}
                    disabled={isProcessing}
                  >
                    Coarse - 64x
                  </button>
                  <button
                    className={`resolution-button ${selectedSize === 128 ? 'active' : ''}`}
                    onClick={() => setSelectedSize(128)}
                    disabled={isProcessing}
                  >
                    Fine - 128x
                  </button>
                </div>
              </div>

              <button
                className="grid-process-button"
                onClick={processGrid}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className="processing-spinner" />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Grid Data'
                )}
              </button>
            </div>
          </div>
        </Panel>

        <PanelResizeHandle className="resize-handle" />

        <Panel defaultSize={35} minSize={20}>
          <div className="grid-sidebar">
            <div className="control-group">
              <h3>Scatterplot Aesthetics</h3>
              
              {/* Color Scheme */}
              <div className="control-item">
                <label>Color Scheme</label>
                <div className="scheme-buttons">
                  {COLOR_SCHEMES.map(scheme => (
                    <button 
                      key={scheme.value}
                      className={`scheme-button ${plotConfig.colorScheme === scheme.value ? 'active' : ''}`}
                      onClick={() => updatePlot({ colorScheme: scheme.value })}
                    >
                      {scheme.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="control-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={plotConfig.invertColorScheme}
                    onChange={(e) => updatePlot({ invertColorScheme: e.target.checked })}
                  />
                  Invert Color Scheme
                </label>
              </div>

              {/* Point Style */}
              <div className="control-item">
                <label>Point Style</label>
                <div className="point-style-buttons">
                  {POINT_STYLES.map(style => (
                    <button
                      key={style.value}
                      className={`style-button ${plotConfig.pointStyle === style.value ? 'active' : ''}`}
                      onClick={() => updatePlot({ pointStyle: style.value })}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Point Size */}
              <div className="control-item">
                <label>Point Size</label>
                <div className="control-input">
                  <input
                    type="range"
                    min="10"
                    max="100"
                    value={plotConfig.pointSize}
                    onChange={(e) => updatePlot({ pointSize: Number(e.target.value) })}
                  />
                  <span className="value">{plotConfig.pointSize}</span>
                </div>
              </div>

              {/* Opacity */}
              <div className="control-item">
                <label>Opacity</label>
                <div className="control-input">
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={plotConfig.opacity}
                    onChange={(e) => updatePlot({ opacity: Number(e.target.value) })}
                  />
                  <span className="value">{plotConfig.opacity.toFixed(1)}</span>
                </div>
              </div>

              {/* Background Color */}
              <div className="control-item">
                <label>Background Color</label>
                <div className="scheme-buttons">
                  <button
                    className={`scheme-button ${plotConfig.backgroundColor === '#ffffff' ? 'active' : ''}`}
                    onClick={() => updatePlot({ backgroundColor: '#ffffff' })}
                  >
                    White
                  </button>
                  <button
                    className={`scheme-button ${plotConfig.backgroundColor === '#f4f4f4' ? 'active' : ''}`}
                    onClick={() => updatePlot({ backgroundColor: '#f4f4f4' })}
                  >
                    L. Gray
                  </button>
                  <button
                    className={`scheme-button ${plotConfig.backgroundColor === '#999999' ? 'active' : ''}`}
                    onClick={() => updatePlot({ backgroundColor: '#999999' })}
                  >
                    D. Gray
                  </button>
                  <button
                    className={`scheme-button ${plotConfig.backgroundColor === '#2d2d2d' ? 'active' : ''}`}
                    onClick={() => updatePlot({ backgroundColor: '#2d2d2d' })}
                  >
                    Dark
                  </button>
                </div>
              </div>

              {/* Plot Title */}
              <div className="control-item">
                <label>Plot Title</label>
                <input
                  type="text"
                  value={plotTitle}
                  onChange={(e) => setPlotTitle(e.target.value)}
                  onKeyPress={handleTitleUpdate}
                  className="text-input"
                  placeholder="Enter plot title"
                  maxLength={50}
                />
              </div>

              {/* Legend Title */}
              <div className="control-item">
                <label>Legend Title</label>
                <input
                  type="text"
                  value={legendTitle}
                  onChange={(e) => setLegendTitle(e.target.value)}
                  onKeyPress={handleTitleUpdate}
                  className="text-input"
                  placeholder="Enter legend title"
                  maxLength={30}
                />
              </div>
            </div>
            {/*  DataTabs  */}
            <div className="control-group">
              <h3>Data Information</h3>
              <DataTabs 
                histogram={state.rawData.histogram}
                stats={state.rawData.stats}
                geoMetadata={state.rawData.geographic_metadata}
              />
            </div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
};
